import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { getWorkspace, updateWorkspace } from "@/lib/workspaces";
import { apiPost } from "@/lib/api";
import { useProjectStore } from "@/stores/project-store";
import { saveScreenshot } from "@/lib/workspace-screenshots";
import { ContentArea } from "./ContentArea";
import { WorkspaceSkeleton } from "./WorkspaceSkeleton";

interface ProxyProject {
  subdomain: string;
  proxyUrl: string;
}

/** How long to wait before the first screenshot (let iframe load). */
const INITIAL_DELAY_MS = 5_000;
/** How often to refresh the stored thumbnail. */
const CAPTURE_INTERVAL_MS = 30_000;

export function WorkspaceLayout() {
  const { id } = useParams<{ id: string }>();
  const { setN8nUrl, setProjectId, setActiveWorkspaceId, setProxyIframeUrl } = useProjectStore();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    setActiveWorkspaceId(id);

    (async () => {
      const workspace = await getWorkspace(id);

      if (cancelled) return;

      if (!workspace) {
        setProjectId(id);
        setLoading(false);
        return;
      }

      setN8nUrl(workspace.n8nUrl);
      setProjectId(workspace.id);

      const existingSubdomain = workspace.proxyUrl ? extractSubdomain(workspace.proxyUrl) : null;
      const proxyStale = !workspace.proxyUrl || existingSubdomain !== workspace.id;

      if (workspace.proxyUrl && !proxyStale) {
        setProxyIframeUrl(workspace.proxyUrl);
        setLoading(false);
        return;
      }

      try {
        const result = await apiPost<ProxyProject>("/api/proxy-projects", { projectId: workspace.id });
        if (cancelled) return;
        setProxyIframeUrl(result.proxyUrl);
        // Fire-and-forget: persist proxyUrl to API
        updateWorkspace(id, { proxyUrl: result.proxyUrl }).catch(() => {});
      } catch {
        if (!cancelled) setProjectId(workspace.id);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, setN8nUrl, setProjectId, setActiveWorkspaceId, setProxyIframeUrl]);

  // ── Periodic screenshot capture ────────────────────────────

  useEffect(() => {
    if (!id) return;

    function onMessage(event: MessageEvent) {
      // When the bridge signals ready, fire a capture shortly after
      if (event.data?.type === "mcp_bridge_ready") {
        setTimeout(fireCapture, 2_000);
        return;
      }

      if (event.data?.type === "screenshot_result" && event.data.dataUrl) {
        saveScreenshot(id!, event.data.dataUrl);
      }
    }

    function fireCapture() {
      const iframe = document.querySelector('iframe[data-workspace-iframe="active"]') as HTMLIFrameElement | null;
      iframe?.contentWindow?.postMessage({ type: "capture_screenshot" }, "*");
    }

    window.addEventListener("message", onMessage);

    // First capture after the iframe has had time to load
    const initial = setTimeout(fireCapture, INITIAL_DELAY_MS);
    // Subsequent captures on an interval
    const interval = setInterval(fireCapture, CAPTURE_INTERVAL_MS);

    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(initial);
      clearInterval(interval);
    };
  }, [id]);

  if (loading) return <WorkspaceSkeleton />;
  return (
    <div className="h-full">
      <ContentArea />
    </div>
  );
}

function extractSubdomain(proxyUrl: string): string | null {
  try {
    const match = new URL(proxyUrl).hostname.match(/^([^.]+)\./);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}
